import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { PanelRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams
} from 'react-router';

import { AccountAddressesContent } from '@/components/account-detail/addresses/AccountAddressesContent';
import { FintrxDataContent } from '@/components/account-detail/fintrx/FintrxDataContent';
import { AccountDetailNav } from '@/components/account-detail/nav/AccountDetailNav';
import { accountNavItems } from '@/components/account-detail/nav/constants';
import { AccountOthersContent } from '@/components/account-detail/others/AccountOthersContent';
import { RightPanel } from '@/components/account-detail/panels/RightPanel';
import { PitchBookDataContent } from '@/components/account-detail/pitchbook/PitchBookDataContent';
import { InvestmentPreferencesContent } from '@/components/account-detail/preferences/InvestmentPreferencesContent';
import { SummaryContent } from '@/components/account-detail/summary/SummaryContent';
import type { AdditionalBreadcrumbItem } from '@/components/Layout';
import { cn } from '@/lib/utils';

const GET_ACCOUNT_DETAIL_HEADER = gql`
  query GetAccountDetailHeader($id: ID!) {
    node(id: $id) {
      ... on AccountNode {
        id
        name
      }
    }
  }
`;

interface AccountDetailHeaderNode {
  id: string;
  name: string;
}

interface GetAccountDetailHeaderData {
  node: AccountDetailHeaderNode | null;
}

interface GetAccountDetailHeaderVariables {
  id: string;
}

export default function AccountDetailPage() {
  const { '*': tabPath, id } = useParams<{ '*': string; id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { addBreadcrumb, clearBreadcrumbs } = useOutletContext<{
    addBreadcrumb?: (item: AdditionalBreadcrumbItem) => void;
    clearBreadcrumbs?: () => void;
  }>();
  const [, setActiveTab] = useState('summary');
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const { data: accountHeaderData } = useQuery<
    GetAccountDetailHeaderData,
    GetAccountDetailHeaderVariables
  >(GET_ACCOUNT_DETAIL_HEADER, {
    skip: !id,
    variables: { id: id ?? '' }
  });
  const accountName = accountHeaderData?.node?.name?.trim() || '-';
  const validTabs = useMemo(() => accountNavItems.map(item => item.id), []);
  const tabFromUrl = tabPath?.split('/')[0] ?? '';
  const resolvedActiveTab = validTabs.includes(
    tabFromUrl as (typeof validTabs)[number]
  )
    ? tabFromUrl
    : 'summary';
  const basePath = `/firm/crm-accounts/${id ?? ''}`;

  useEffect(() => {
    if (!id) {
      return;
    }
    if (
      !tabFromUrl ||
      !validTabs.includes(tabFromUrl as (typeof validTabs)[number])
    ) {
      navigate(`${basePath}/summary`, { replace: true });
    }
  }, [basePath, id, navigate, tabFromUrl, validTabs]);

  useEffect(() => {
    if (!addBreadcrumb || accountName === '-') {
      return;
    }

    clearBreadcrumbs?.();
    addBreadcrumb({ label: accountName });

    return () => {
      clearBreadcrumbs?.();
    };
  }, [accountName, addBreadcrumb, clearBreadcrumbs, pathname]);

  return (
    <div
      className={cn(
        'flex h-[calc(100dvh-64px)] flex-col',
        rightPanelOpen
          ? 'md:grid md:grid-cols-[210px_1fr_auto]'
          : 'md:grid md:grid-cols-[210px_1fr_70px]'
      )}
    >
      {/* Account Detail Sidebar */}
      <AccountDetailNav
        activeTab={resolvedActiveTab}
        basePath={basePath}
        items={accountNavItems}
        onTabChange={setActiveTab}
      />

      {/* Main Content Area */}
      <div className="min-h-0 flex-1 overflow-auto bg-white dark:bg-gray-950">
        <div className="bg-sidebar sticky top-0 z-1 flex h-12.25 items-center border-b px-5 py-1.5 md:pl-14">
          <div className="text-lg font-semibold">{accountName}</div>
        </div>
        <div className="p-5 md:pl-14">
          {resolvedActiveTab === 'summary' && id && (
            <SummaryContent accountId={id} />
          )}
          {resolvedActiveTab === 'investment-preferences' && id && (
            <InvestmentPreferencesContent accountId={id} />
          )}
          {resolvedActiveTab === 'pitchbook-data' && id && (
            <PitchBookDataContent accountId={id} />
          )}
          {resolvedActiveTab === 'fintrx-data' && id && (
            <FintrxDataContent accountId={id} />
          )}
          {resolvedActiveTab === 'addresses' && id && (
            <AccountAddressesContent accountId={id} />
          )}
          {resolvedActiveTab === 'others' && id && (
            <AccountOthersContent accountId={id} />
          )}
          {resolvedActiveTab !== 'summary' &&
            resolvedActiveTab !== 'investment-preferences' &&
            resolvedActiveTab !== 'pitchbook-data' &&
            resolvedActiveTab !== 'fintrx-data' &&
            resolvedActiveTab !== 'addresses' &&
            resolvedActiveTab !== 'others' && (
              <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-gray-600 dark:text-gray-400">
                  Content for {resolvedActiveTab} (account {id ?? '—'}) tab will
                  be displayed here
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Right Panel or Toggle Button */}
      <div className="hidden md:contents">
        {rightPanelOpen ? (
          <RightPanel
            accountId={id ?? ''}
            isOpen={rightPanelOpen}
            onClose={() => {
              setRightPanelOpen(false);
            }}
          />
        ) : (
          <button
            className="bg-card sticky top-5 grid size-12.5 place-content-center rounded-2xl border-l shadow-lg"
            onClick={() => {
              setRightPanelOpen(true);
            }}
            title="Open panel"
            type="button"
          >
            <PanelRightIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
}
